import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import LinearProgress from '@mui/material/LinearProgress';
import { toast } from '../../../common/toast';
import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import AssignmentDialog from '../AssignmentDialog.js';
import { COUNTY_CODES } from '../../../common/mappings.js';
import { useNYData } from '../../../common/NyDataObject.js';
import { useUser } from '../../UserContext.js';
import logger from '../../../utils/logger';
import buildTotalsRow from './TotalsRow.js';
import { getColumns } from './columnConfigs.js';
import TaskToolbar from './TaskToolbar.js';
import {
  createEmptyRowSelection,
  getSelectedRowIds,
} from '../utils/gridRowSelectionModel.js';
import { withSelectAllAwareCheckbox } from '../utils/gridSelectAllCheckbox.js';
import {
  getEffectiveViewMode,
  getGroupingEndpoint,
  getSelectionSearchConfig,
  transformGroupingData,
  handleGroupingNavigation,
  clearGroupingState,
} from './groupingLogic.js';
import { getDefaultYear, getTaxYearOptions } from '../../../common/helper_functions.js';

const TASK_GRID_SLOTS = withSelectAllAwareCheckbox({
  toolbar: TaskToolbar,
  loadingOverlay: LinearProgress,
});

// Helper function to get county name from county code
const getCountyName = (countyCode) => {
  if (!countyCode) return 'Unknown';
  return COUNTY_CODES[countyCode] || 'Unknown';
};


// Shared status calculation
const calculateStatus = (completed, total) => {
  if (total === 0) return 'No Tasks';
  if (completed === total) return 'All Done';
  if (completed === 0) return 'Incomplete';
  return 'Partial';
};

const summarizeGridRow = (row) => ({
  id: row.id,
  countyCode: row.countyCode,
  muniCode: row.muniCode,
  userId: row.userId,
  ParcelID: row.ParcelID || row.parcel_id,
  ReviewType: row.ReviewType,
  DateCompleted: row.DateCompleted,
});

const summarizeTask = (task) => ({
  id: task.id,
  ParcelID: task.ParcelID || task.parcel_id,
  ReviewType: task.ReviewType,
  Year: task.Year,
  DateCompleted: task.DateCompleted,
});


const NewTaskGrid = ({ isAdmin = false }) => {
  const { user } = useUser();
  const [countyCounts, setCountyCounts] = useState([]);
  const [muniCounts, setMuniCounts] = useState([]);
  const [muniTasks, setMuniTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearFilter, setYearFilter] = useState(() => getDefaultYear());
  const [stageFilter, setStageFilter] = useState('incomplete');
  const [reviewTypeFilter, setReviewTypeFilter] = useState(['all']);
  const [viewMode, setViewMode] = useState('county'); // 'county' | 'municipality' | 'tasks'
  const [activeCounty, setActiveCounty] = useState(null); // { code, name }
  const [activeMuni, setActiveMuni] = useState(null); // { code, name }
  const [activeUser, setActiveUser] = useState(null); // { id, name }
  const [selectionModel, setSelectionModel] = useState(createEmptyRowSelection);
  const [groupingType, setGroupingType] = useState('County'); // 'County' | 'Municipality' | 'User'
  const [myTasksToggle, setMyTasksToggle] = useState(true);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignmentTasks, setAssignmentTasks] = useState([]);

  const { getLocationNameFromMuniCode } = useNYData();

  // Generate available years for year tabs
  const availableYears = useMemo(() => getTaxYearOptions().map((o) => o.value).reverse(), []);

  // Normalize filter params to single string values
  const normalizeFilterParam = useCallback((filter) => {
    if (Array.isArray(filter)) {
      return filter.includes('all') ? 'all' : (filter[0] || 'all');
    }
    return filter || 'all';
  }, []);

  // Memoize effective view mode based on groupingType
  const effectiveViewMode = useMemo(() => {
    return getEffectiveViewMode(viewMode, groupingType);
  }, [viewMode, groupingType]);

  // Create state setters object for cleaner function calls
  const stateSetters = useMemo(() => ({
    setActiveCounty,
    setActiveUser,
    setViewMode,
    setMuniCounts,
    setMuniTasks,
    setActiveMuni,
  }), []);

  // Wrapper for setGroupingType that clears grouping-specific state
  const handleSetGroupingType = useCallback((newGroupingType) => {
    setGroupingType(newGroupingType);
    clearGroupingState(newGroupingType, stateSetters);
    // Clear data arrays and set loading immediately to prevent showing default values
    setCountyCounts([]);
    setMuniCounts([]);
    setMuniTasks([]);
    setLoading(true);
  }, [stateSetters]);

  // Handle "My Tasks" as an assignee filter without changing grouping/navigation level
  const handleMyTasksToggle = useCallback((enabled) => {
    setMyTasksToggle(enabled);
    setSelectionModel(createEmptyRowSelection());
    if (enabled) {
      setViewMode('county');
      setActiveCounty(null);
      setActiveMuni(null);
      setActiveUser(null);
      setGroupingType((current) => (current === 'User' ? 'County' : current));
      setCountyCounts([]);
      setMuniCounts([]);
      setMuniTasks([]);
      setLoading(true);
      return;
    }
    setCountyCounts([]);
    setMuniCounts([]);
    setMuniTasks([]);
    setLoading(true);
  }, []);

  const myTasksUserId = myTasksToggle && user?.userId ? user.userId : null;

  // Unified fetch function for all view modes, get the info we render in the datagrid rows
  const fetchData = useCallback(async () => {
    if (myTasksToggle && !user?.userId) {
      return;
    }

    const stageParam = normalizeFilterParam(stageFilter);
    const reviewParam = normalizeFilterParam(reviewTypeFilter);
    const requestParams = { Year: yearFilter, Stage: stageParam, ReviewType: reviewParam };

    // Get endpoint configuration from grouping logic
    const endpointConfig = getGroupingEndpoint(
      effectiveViewMode,
      groupingType,
      activeCounty,
      activeUser,
      activeMuni,
      myTasksUserId,
    );

    if (!endpointConfig) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Merge endpoint-specific params with base request params
      const finalParams = { ...requestParams, ...endpointConfig.params };
      
      // fetch data from the appropriate endpoint, defined in the groupingLogic.js file
      const response = await axiosInstance.post(endpointConfig.endpoint, finalParams);

      if (response?.data?.success) {
        if (effectiveViewMode === 'tasks') {
          // Handle tasks view
          const tasks = (response.data.tasks || []).map((task, idx) => ({
            id: idx + 1,
            ...task,
            villageRun: task.CaseType === 'Village' ? 1 : 0,
            status: task.DateCompleted ? 'Completed' : 'Incomplete',
          }));
          setMuniTasks(tasks);
        } else if (effectiveViewMode === 'county') {
          // Handle county-level grouping (County, User)
          const transformedData = transformGroupingData(
            response.data.counts || [],
            groupingType,
            getCountyName,
            getLocationNameFromMuniCode
          );
          setCountyCounts(transformedData);
        } else if (effectiveViewMode === 'municipality') {
          // Handle municipality view
          const muniData = transformGroupingData(
            response.data.counts || [],
            'Municipality',
            getCountyName,
            getLocationNameFromMuniCode
          );
          // Adjust IDs based on context
          const adjustedMuniData = muniData.map((row, idx) => {
            let idPrefix = 'muni-all';
            if (activeCounty?.code) {
              idPrefix = `muni-${activeCounty.code}`;
            } else if (activeUser?.id) {
              idPrefix = `muni-user-${activeUser.id}`;
            }
            return {
              ...row,
              id: `${idPrefix}-${idx}`,
            };
          });
          setMuniCounts(adjustedMuniData);
        }
      } else {
        setError(response?.data?.error || 'Failed to load data');
      }
    } catch (err) {
      setError('Failed to load data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [myTasksToggle, myTasksUserId, user?.userId, effectiveViewMode, yearFilter, stageFilter, reviewTypeFilter, activeCounty?.code, activeMuni?.code, activeUser?.id, groupingType, normalizeFilterParam]);

  // Single useEffect to fetch data when filters or view mode changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate totals for pinned row
  const displayedRows = effectiveViewMode === 'county' ? countyCounts : effectiveViewMode === 'municipality' ? muniCounts : muniTasks;

  const pinnedTotalsRow = useMemo(() => {
    if (effectiveViewMode === 'tasks') return null;
    return buildTotalsRow(displayedRows, effectiveViewMode, calculateStatus, groupingType);
  }, [displayedRows, effectiveViewMode, groupingType]);

  // Derive selected rows from selection model
  const selectedRows = useMemo(() => {
    const selectedIdSet = new Set(getSelectedRowIds(selectionModel).map(String));
    return displayedRows.filter((row) => selectedIdSet.has(String(row.id)));
  }, [displayedRows, selectionModel]);

  // Unified handler for navigation clicks - just sets state, fetchData handles loading
  const handleNavigationClick = useCallback((type, code, name) => {
    if (!code && type !== 'user') return;
    if (type === 'county') {
      setActiveCounty({ code, name });
      setViewMode('municipality');
      setMuniCounts([]);
      setMuniTasks([]);
      setActiveMuni(null);
    } else if (type === 'municipality') {
      setActiveMuni({ code, name });
      setViewMode('tasks');
      setMuniTasks([]);
    } else if (type === 'user') {
      // When manually selecting a user, turn off "My Tasks" toggle
      setMyTasksToggle(false);
      handleGroupingNavigation(
        groupingType,
        { userId: code, userName: name },
        stateSetters
      );
    }
  }, [groupingType, stateSetters]);

  // Wrapper handlers for backward compatibility
  const handleCountyClick = useCallback(
    (countyCode, countyName) => handleNavigationClick('county', countyCode, countyName),
    [handleNavigationClick]
  );

  const handleMuniClick = useCallback(
    (muniCode, muniName) => handleNavigationClick('municipality', muniCode, muniName),
    [handleNavigationClick]
  );

  const handleUserClick = useCallback(
    (userId, userName) => handleNavigationClick('user', userId, userName),
    [handleNavigationClick]
  );

  const fetchTasksForSelection = useCallback(async () => {
    const stageParam = normalizeFilterParam(stageFilter);
    const reviewParam = normalizeFilterParam(reviewTypeFilter);
    const searchConfig = getSelectionSearchConfig(effectiveViewMode, groupingType);
    const searchValues = [...new Set(selectedRows.map(searchConfig.extractValue).filter(Boolean))];
    const requestPayload = {
      Year: yearFilter,
      Stage: stageParam,
      ReviewType: reviewParam,
    };

    if (searchValues.length === 0) {
      logger.log('[TaskGrid] fetchTasksForSelection aborted: no codes extracted from selection', {
        selectedRowCount: selectedRows.length,
        selectedRows: selectedRows.map(summarizeGridRow),
        searchConfig,
      });
      toast.error('Could not resolve selected rows for assignment.');
      return [];
    }

    const fetchForPayload = async (payload) => {
      const response = await axiosInstance.post('/load_tasks_by_selection', payload);
      if (!response?.data?.success) {
        logger.log('[TaskGrid] fetchTasksForSelection failed', response?.data);
        return [];
      }
      return response.data.tasks || [];
    };

    logger.log('[TaskGrid] fetchTasksForSelection request', {
      effectiveViewMode,
      groupingType,
      selectedRowCount: selectedRows.length,
      searchValues,
    });

    let rawTasks = [];
    if (searchConfig.perValue) {
      const responses = await Promise.all(
        searchValues.map((value) =>
          fetchForPayload({ ...requestPayload, [searchConfig.field]: value })
        )
      );
      rawTasks = responses.flat();
    } else {
      const assigneeUserId = activeUser?.id || myTasksUserId;
      if (assigneeUserId) {
        requestPayload.userId = assigneeUserId;
      }
      requestPayload[searchConfig.field] = searchValues;
      rawTasks = await fetchForPayload(requestPayload);
    }

    const tasks = rawTasks
      .filter((task) => !task.DateCompleted)
      .map((task, idx) => ({
        id: idx + 1,
        ...task,
        villageRun: task.CaseType === 'Village' ? 1 : 0,
      }));

    logger.log('[TaskGrid] fetchTasksForSelection response', {
      count: tasks.length,
      tasks: tasks.map(summarizeTask),
    });

    return tasks;
  }, [
    effectiveViewMode,
    groupingType,
    yearFilter,
    stageFilter,
    reviewTypeFilter,
    activeUser?.id,
    myTasksUserId,
    selectedRows,
    normalizeFilterParam,
  ]);

  const handleAssignClick = useCallback(async () => {
    logger.log('[TaskGrid] handleAssignClick start', {
      effectiveViewMode,
      groupingType,
      selectionModelCount: getSelectedRowIds(selectionModel).length,
      selectionModel: getSelectedRowIds(selectionModel),
      selectedRowCount: selectedRows.length,
      selectedRows: selectedRows.map(summarizeGridRow),
      displayedRowCount: displayedRows.length,
    });

    let tasksToAssign = [];

    if (effectiveViewMode === 'tasks') {
      tasksToAssign = selectedRows
        .filter((row) => !row.DateCompleted)
        .map((row, idx) => ({
          ...row,
          ParcelID: row.ParcelID || row.parcel_id,
          id: row.id ?? idx + 1,
          villageRun: row.villageRun ?? (row.CaseType === 'Village' ? 1 : 0),
        }));
      logger.log('[TaskGrid] handleAssignClick tasks view path', {
        tasksToAssignCount: tasksToAssign.length,
        tasksToAssign: tasksToAssign.map(summarizeTask),
      });
    } else {
      tasksToAssign = await fetchTasksForSelection();
      logger.log('[TaskGrid] handleAssignClick grouped view path', {
        tasksToAssignCount: tasksToAssign.length,
        tasksToAssign: tasksToAssign.map(summarizeTask),
      });
    }

    if (!tasksToAssign.length) {
      toast.info('No incomplete tasks found in selection. Completed tasks cannot be assigned.');
      return;
    }

    logger.log('[TaskGrid] opening assignment dialog', {
      selectedRowCount: selectedRows.length,
      assignmentTaskCount: tasksToAssign.length,
      selectedRows: selectedRows.map(summarizeGridRow),
      assignmentTasks: tasksToAssign.map(summarizeTask),
    });

    setAssignmentTasks(tasksToAssign);
    setAssignmentDialogOpen(true);
  }, [effectiveViewMode, selectedRows, selectionModel, displayedRows.length, groupingType, fetchTasksForSelection]);

  const handleSelectionChange = useCallback((newSelection) => {
    logger.log('[TaskGrid] selection changed', {
      effectiveViewMode,
      groupingType,
      newSelectionCount: getSelectedRowIds(newSelection).length,
      newSelection: getSelectedRowIds(newSelection),
    });
    setSelectionModel(newSelection);
  }, [effectiveViewMode, groupingType]);

  // Exit the "zoomed in" view of the grid (tasks, municipality, county) and go back to the previous view
  const handleBack = useCallback(() => {
    if (effectiveViewMode === 'tasks') {
      // From tasks view, go back to municipality view
      setViewMode('municipality');
      setActiveMuni(null);
      setMuniTasks([]);
    } else if (effectiveViewMode === 'municipality') {
      if (activeUser) {
        setViewMode('county');
        setActiveUser(null);
        setMuniCounts([]);
      } else if (activeCounty) {
        setViewMode('county');
        setActiveCounty(null);
        setMuniCounts([]);
      } else if (groupingType === 'Municipality') {
        setViewMode('county');
        setGroupingType('County');
        setMuniCounts([]);
      }
      setActiveMuni(null);
      setMuniTasks([]);
    }
  }, [effectiveViewMode, groupingType, activeCounty, activeUser]);

  // Dynamic columns by zoom level
  const columns = useMemo(
    () => getColumns(effectiveViewMode, { 
      handleCountyClick, 
      handleMuniClick, 
      handleUserClick,
      groupingType
    }),
    [effectiveViewMode, handleCountyClick, handleMuniClick, handleUserClick, groupingType]
  );

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Card sx={{ px: 0, pb: 0, mb: 0, height: '100%', maxHeight: '615px', width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%', p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          key={`${effectiveViewMode}-${groupingType}`}
          rows={displayedRows}
          columns={columns}
          hideFooter
          density="compact"
          checkboxSelection
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnFilter={false}
          columnBufferPx={150}
          rowBufferPx={200}
          loading={loading}
          localeText={{
            noRowsLabel: 'No Tasks available for selected filters',
          }}
          pinnedRows={pinnedTotalsRow ? { bottom: [pinnedTotalsRow] } : undefined}
          isRowSelectable={(params) => params.id !== 'totals'}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleSelectionChange}
          slots={TASK_GRID_SLOTS}
          slotProps={{
            toolbar: {
              viewMode: effectiveViewMode,
              activeCounty,
              activeMuni,
              activeUser,
              handleBack,
              stageFilter,
              setStageFilter,
              reviewTypeFilter,
              setReviewTypeFilter,
              yearFilter,
              setYearFilter,
              availableYears,
              selectedRows,
              groupingType,
              setGroupingType: handleSetGroupingType,
              myTasksToggle,
              setMyTasksToggle: handleMyTasksToggle,
              onAssignClick: handleAssignClick,
            },
            ...(effectiveViewMode === 'municipality' && {
              filterPanel: {
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'right',
                },
              },
            }),
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.875rem',
              fontWeight: 'bold',
            },
            '& [data-id="totals"]': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '& .MuiDataGrid-cell': {
                fontWeight: 'bold !important',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                paddingTop: '4px',
                paddingBottom: '4px',
              }
            },
            '& .MuiDataGrid-pinnedRows--bottom': {
              '& .MuiDataGrid-row': {
                '& .MuiDataGrid-cell': {
                  paddingTop: '4px',
                  paddingBottom: '4px',
                }
              }
            },
            '& .MuiDataGrid-main': {
              width: 0,
              minWidth: '100%',
              flex: 1,
              minHeight: 0,
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto',
            },
            width: '100%',
            minWidth: 0,
            flex: 1,
            minHeight: 0,
          }}
          showToolbar
          disableRowSelectionExcludeModel />
      </Box>
      <AssignmentDialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        selectedItems={assignmentTasks}
        isAdmin={isAdmin}
        tasks={assignmentTasks}
        onAssign={() => {
          setSelectionModel(createEmptyRowSelection());
          setAssignmentDialogOpen(false);
          fetchData();
        }}
      />
    </Card>
  );
};

export default NewTaskGrid;
